import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN, stickerButton } from "../../theme";
import { COUNTING_ENGINE_THEME, countingChoices, countingFlashDelay, countingTarget } from "../../../../data/countingLadderCatalog";
import { ConfettiBurst } from "../../feedback/Celebration";
import { CountingPanel, CountingPiece, CountingPieceGrid, CountingRoot, EyeIcon, NumberCard, PanelChip, darkenHex, useCompleteOnce } from "../CountingPiece";

/**
 * Subitising: the blueberries show for a beat, a leaf slides over them,
 * then the child taps how many they saw. Wrong answers re-flash the set.
 */
const FlashSetGame = ({ activity, sfx, onComplete, onWrong, reduceMotion = false }) => {
  const target = countingTarget(activity);
  const accent = COUNTING_ENGINE_THEME.flashSet.accent;
  const delayMs = countingFlashDelay(activity) * 1000;
  const choices = countingChoices(target, Math.min(activity.maximumNumber, 10));
  const [hidden, setHidden] = useState(false);
  const [flashes, setFlashes] = useState(0);
  const [picked, setPicked] = useState(null); // { value, ok }
  const [burst, setBurst] = useState(0);
  const timer = useRef(null);
  const complete = useCompleteOnce(onComplete);
  const solved = picked?.ok === true;

  const scheduleHide = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setHidden(true), delayMs);
  }, [delayMs]);

  useEffect(() => {
    scheduleHide();
    return () => clearTimeout(timer.current);
  }, [scheduleHide, activity.id]);

  const flashAgain = () => {
    if (solved) return;
    sfx?.("whoosh", { volume: 0.4 });
    setHidden(false);
    setFlashes((n) => n + 1);
    scheduleHide();
  };

  const choose = (value) => {
    if (solved) return;
    if (value === target) {
      setPicked({ value, ok: true });
      setHidden(false);
      sfx?.("correct");
      setBurst((b) => b + 1);
      complete();
      return;
    }
    setPicked({ value, ok: false });
    onWrong?.();
    setTimeout(() => setPicked((p) => (p?.value === value && !p.ok ? null : p)), 600);
    flashAgain();
  };

  const chipText = solved ? "You saw it" : hidden ? "How many?" : "Look closely";

  return (
    <CountingRoot>
      <CountingPanel grow tint={accent} chip={<PanelChip text={chipText} accent={accent} />} style={{ overflow: "hidden" }}>
        <motion.div
          key={`flash-${flashes}`}
          initial={reduceMotion ? false : { opacity: 0, scale: 0.9 }}
          animate={{ opacity: hidden ? 0 : 1, scale: 1, filter: hidden ? "blur(6px)" : "blur(0px)" }}
          transition={reduceMotion ? { duration: 0 } : { duration: 0.28, delay: hidden ? 0.16 : 0 }}
          style={{ flex: "1 1 0%", minHeight: 0, width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <CountingPieceGrid count={target} asset="blueberry" reduceMotion={reduceMotion} pop={!reduceMotion} labelPrefix="Blueberry" />
        </motion.div>
        <AnimatePresence>
          {hidden && (
            <motion.div
              key="cover"
              aria-label="Leaf covering the blueberries"
              role="img"
              initial={reduceMotion ? false : { x: "115%", rotate: 26, opacity: 1 }}
              animate={{ x: 0, rotate: 0, opacity: 1 }}
              exit={reduceMotion ? { opacity: 0 } : { x: "-115%", rotate: -22, opacity: 1 }}
              transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 190, damping: 22 }}
              style={{ position: "absolute", inset: 0, zIndex: 1, display: "grid", placeItems: "center", pointerEvents: "none" }}
            >
              <CountingPiece asset="leaf" variant="bare" size="min(calc(var(--tile) * 4.4), 92%)" lifted reduceMotion={reduceMotion} />
            </motion.div>
          )}
        </AnimatePresence>
        {burst > 0 && <ConfettiBurst key={`burst-${burst}`} count={28} originY={40} reduceMotion={reduceMotion} />}
      </CountingPanel>

      <div style={{ minHeight: "calc(var(--tile) * 1.4)", display: "flex", alignItems: "center", justifyContent: "center", gap: "calc(var(--tile) * 0.16)", flexWrap: "wrap" }}>
        {(hidden || solved) && (
          <motion.div
            key="choices"
            initial={reduceMotion ? false : { y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.22 }}
            style={{ display: "flex", flexWrap: "wrap", gap: "calc(var(--tile) * 0.16)", justifyContent: "center", alignItems: "center" }}
          >
            {choices.map((value) => (
              <NumberCard
                key={value}
                accent={accent}
                reduceMotion={reduceMotion}
                disabled={solved}
                state={picked?.value === value ? (picked.ok ? "correct" : "wrong") : "idle"}
                onClick={() => choose(value)}
              >
                {value}
              </NumberCard>
            ))}
            {hidden && !solved && (
              <motion.button
                type="button"
                className="counting-btn"
                onClick={flashAgain}
                initial={reduceMotion ? false : { scale: 0.6, rotate: -6 }}
                animate={{ scale: 1, rotate: -3 }}
                whileTap={reduceMotion ? undefined : { scale: 0.95, y: 3 }}
                transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 380, damping: 18, delay: 0.1 }}
                style={{
                  ...stickerButton({ bg: LEARN.color.white, deep: darkenHex(accent, 0.15), size: "md", color: LEARN.color.ink }),
                  border: `3px solid ${accent}`, display: "inline-flex", alignItems: "center", gap: 8, marginLeft: "calc(var(--tile) * 0.1)",
                }}
              >
                <EyeIcon size={20} color={darkenHex(accent, 0.2)} />
                Peek again
              </motion.button>
            )}
          </motion.div>
        )}
      </div>
    </CountingRoot>
  );
};

export default FlashSetGame;
